import { BaseModel } from './../../../../../../../core/src/lib/models/base.model';
import { UntypedFormControl, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, OnChanges, ViewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { MenuItem, SelectItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { AppointmentDataDaytype, AppointmentModel, AppointmentType } from 'projects/core/src/lib/models/appointment.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';

enum AppointmentCalendarClickMode {
  Simple = 'simple',
  Advanced = 'advanced',
}

enum AppointmentCalendarDayMode {
  Holiday = 'holiday',
  Hiringday = 'hiringday',
  Availability = 'availability',
}

export enum AppointmentCalendarType {
  Dates = 'dates',
  Availabilities = 'availabilities',
}

export enum AppointmentDayEvent {
  Create = 'create',
  Change = 'change',
  Delete = 'delete',
}

class FormLocationModel extends LocationModel {
  public control: UntypedFormControl;
}

class FormStateModel extends BaseModel {
  public name: string;
  public control: UntypedFormControl;
}

export interface AppointmentDayEventType {
  day: AppointmentModel;
  event: AppointmentDayEvent;
  settings?: any;
}

@Component({
  selector: 'app-appointment-calendar',
  templateUrl: './appointment-calendar.component.html',
  styleUrls: ['./appointment-calendar.component.scss']
})
export class AppointmentCalendarComponent implements OnInit, OnChanges {

  @ViewChild('menu') menu: Menu;

  @Input() type: AppointmentCalendarType = AppointmentCalendarType.Dates;
  @Input() readonly = false;
  @Input() size = 3;
  @Input() min: string;
  @Input() max: string;
  @Input() minDate: any;
  @Input() maxMonths: number;
  @Input() inEdit: boolean;
  @Input() days: Array<AppointmentModel> = [];
  @Input() specialdays: Array<AppointmentModel> = [];
  @Input() locations: Array<FormLocationModel> = [];
  @Input() states: Array<any> = [];
  @Input() timing: any;

  @Output() onDayChange = new EventEmitter<AppointmentDayEventType>();

  public clickMode: any;
  public dayMode: any = null;
  public months: Array<Date> = [];
  public actualMonth = 0;
  public calandarMinDate: any;
  public dayMapping: any = {};
  public items: Array<MenuItem> = [];
  public settingsVisible = false;
  public settingsForm: UntypedFormGroup = this.fb.group({
    daytype: new UntypedFormControl('')
  });
  public holidaySettingsVisible = false;
  public holidaySettingsForm: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl('', [Validators.required])
  });
  public timingInfo: any = {
    full: '-',
    morning: '-',
    afternoon: '-',
  };
  public optionsDayType: Array<SelectItem> = [
    {
      label: this.transloco.translate('common.labels.allday'),
      value: AppointmentDataDaytype.Allday
    },
    {
      label: this.transloco.translate('common.labels.morning'),
      value: AppointmentDataDaytype.Morning
    },
    {
      label: this.transloco.translate('common.labels.afternoon'),
      value: AppointmentDataDaytype.Afternoon
    },
  ];
  public settingsAppointment: AppointmentModel = null;
  public settingsEvent: AppointmentDayEvent;
  public holidaySettingsAppointment: AppointmentModel = null;
  public holidaySettingsEvent: AppointmentDayEvent;

  public dayModeOptions: Array<any> = [];
  public clickModeOptions: Array<any> = [
    {
      key: AppointmentCalendarClickMode.Simple,
      name: this.transloco.translate('appointments.calendar.simple_mode')
    },
    {
      key: AppointmentCalendarClickMode.Advanced,
      name: this.transloco.translate('appointments.calendar.advanced_mode')
    },
  ];
  public optionsStates: Array<FormStateModel> = [];

  public AppointmentCalendarClickMode = AppointmentCalendarClickMode;
  public AppointmentCalendarDayMode = AppointmentCalendarDayMode;
  public AppointmentCalendarType = AppointmentCalendarType;

  // public value: Date;

  constructor(
    private ui: UiService,
    private fb: UntypedFormBuilder,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    // set day mode
    if (this.type === AppointmentCalendarType.Dates) {
      this.dayModeOptions = [
        {
          key: AppointmentCalendarDayMode.Hiringday,
          name: this.transloco.translate('common.labels.hiringday')
        },
        {
          key: AppointmentCalendarDayMode.Holiday,
          name: this.transloco.translate('common.labels.holiday')
        },
      ];
      this.dayMode = this.dayModeOptions.find(option => option.key === AppointmentCalendarDayMode.Hiringday);
    } else {
      this.dayModeOptions = [
        {
          key: AppointmentCalendarDayMode.Availability,
          name: this.transloco.translate('common.labels.availability')
        },
      ];
      this.dayMode = this.dayModeOptions.find(option => option.key === AppointmentCalendarDayMode.Availability);
    }
    // set click mode
    this.clickMode = this.clickModeOptions.find(option => option.key === AppointmentCalendarClickMode.Simple);
    const clickMode = localStorage.getItem('appointments-mode') as AppointmentCalendarClickMode;
    if (clickMode !== undefined && clickMode !== null && this.type === AppointmentCalendarType.Availabilities) {
      this.clickMode = this.clickModeOptions.find(option => option.key === clickMode);
    }
  }

  ngOnChanges(): void {
    setTimeout(() => {
      this.buildStates();
      this.buildMonths();
      this.buildDayMapping();
      this.initForm();
      this.buildTimingInfos();
    }, 0);
  }

  public onClickModeChange(event: any): void {
    localStorage.setItem('appointments-mode', event.value?.key);
  }

  public onDayClicked(domEvent: any, date: any): void {
    if (this.inEdit === true) {
      if (this.isAvailabilityMode() === true) {
        this.onAvailabilityClicked(domEvent, date);
      } else if (this.isSpecialdaysMode() === true) {
        this.onSpecialdayClicked(date);
      }
    }
  }

  private onAvailabilityClicked(domEvent: any, date: any): void {
    let event = null;
    const settings = {};
    let appointment = this.getAppointment(date);
    if (appointment?.used > 0) {
      this.ui.toastInfo(this.transloco.translate('appointments.calendar.appointment_blocked'));
      return;
    }
    if (this.clickMode?.key === AppointmentCalendarClickMode.Simple) {
      if (this.isAppointmentSelectable(appointment)) {
        // create
        appointment = this.getNewAppointment(date);
        event = AppointmentDayEvent.Create;
      } else {
        if (this.isAppointmentDeletable(appointment)) {
          // delete
          event = AppointmentDayEvent.Delete;
        } else {
          if (!appointment.isNationalHoliday()) {
            this.ui.toastInfo(this.transloco.translate('appointments.calendar.appointment_blocked'));
          }
          return;
        }
      }
      if (event !== null) {
        this.emitDayEvent({
          day: appointment,
          event,
          settings
        });
      }
    } else {
      if (this.isAppointmentSelectable(appointment)) {
        this.items = [
          {
            label: this.transloco.translate('common.labels.allday'),
            icon: 'pi pi-calendar',
            command: () => {
              this.emitDayEvent({
                day: this.getNewAppointment(date),
                event: AppointmentDayEvent.Create,
                settings
              });
            }
          },
          {
            label: this.transloco.translate('common.labels.morning'),
            icon: 'pi pi-calendar-minus',
            command: () => {
              appointment = this.getNewAppointment(date);
              appointment.setMorning();
              this.emitDayEvent({
                day: appointment,
                event: AppointmentDayEvent.Create,
                settings
              });
            }
          },
          {
            label: this.transloco.translate('common.labels.afternoon'),
            icon: 'pi pi-calendar-minus',
            command: () => {
              appointment = this.getNewAppointment(date);
              appointment.setAfternoon();
              this.emitDayEvent({
                day: appointment,
                event: AppointmentDayEvent.Create,
                settings
              });
            }
          },
          {
            label: this.transloco.translate('common.labels.set_details'),
            icon: 'pi pi-pencil',
            command: () => {
              appointment = this.getNewAppointment(date);
              if (appointment.updateable === false) {
                this.ui.toastInfo(this.transloco.translate('appointments.calendar.appointment_blocked'));
                return;
              }
              this.showSettings(appointment, AppointmentDayEvent.Create);
            }
          },
        ];
        this.menu.toggle(domEvent);
      } else if (appointment !== null && !appointment.isNationalHoliday()) {
        this.items = [
          {
            label: this.transloco.translate('common.labels.edit'),
            icon: 'pi pi-pencil',
            command: () => {
              if (appointment.updateable === false) {
                this.ui.toastInfo(this.transloco.translate('appointments.calendar.appointment_blocked'));
                return;
              }
              this.showSettings(appointment, AppointmentDayEvent.Change);
            }
          },
          {
            label: this.transloco.translate('common.labels.delete'),
            icon: 'pi pi-trash',
            styleClass: 'danger-button',
            command: () => {
              if (appointment.deletable === false) {
                this.ui.toastInfo(this.transloco.translate('appointments.calendar.appointment_blocked'));
                return;
              }
              // delete
              event = AppointmentDayEvent.Delete;
              this.emitDayEvent({
                day: appointment,
                event,
                settings
              });
            }
          },
        ];
        this.menu.toggle(domEvent);
      }
    }

  }

  private onSpecialdayClicked(date: any): void {
    let event = null;
    const settings = {};
    let appointment = this.getAppointment(date);
    if (this.isAppointmentSelectable(appointment)) {
      // create
      event = AppointmentDayEvent.Create;
      appointment = this.getNewAppointment(date);
      if (this.dayMode?.key === AppointmentCalendarDayMode.Holiday) {
        this.showHolidaySettings(appointment, event);
        return;
      }
    } else if (this.isAppointmentDeletable(appointment)) {
      event = AppointmentDayEvent.Delete;
    }
    if (event !== null) {
      this.emitDayEvent({
        day: appointment,
        event,
        settings
      });
    }
  }

  private emitDayEvent(event: AppointmentDayEventType): void {
    this.onDayChange.emit(event);
  }

  public getDayClass(date: any): string {
    const appointment = this.getAppointment(date);
    if (appointment !== null) {
      let type: string = appointment.type;
      if (appointment?.used > 0) {
        type += '-booked';
      } else if (appointment.isLocalHoliday()) {
        type += '-local';
      } else if (appointment.isMorning()) {
        type += '-morning';
      } else if (appointment.isAfternoon()) {
        type += '-afternoon';
      }
      return type;
    }
    return '';
  }

  public getDayTooltip(date: any): string {
    const appointment = this.getAppointment(date);
    if (appointment !== null) {
      return appointment.getTooltipText();
    }
    return '';
  }

  public previousMonth(): void {
    if (!this.isFirstMonth()) {
      this.actualMonth--;
      this.buildMonths();
    }
  }

  public nextMonth(): void {
    if (!this.isLastMonth()) {
      this.actualMonth++;
      this.buildMonths();
    }
  }

  public isFirstMonth(): boolean {
    return this.actualMonth === 0;
  }

  public isLastMonth(): boolean {
    return this.actualMonth === this.maxMonths - this.size - 1;
  }

  // availability settings

  public showSettings(appointment: AppointmentModel, event: AppointmentDayEvent): void {
    this.settingsAppointment = appointment;
    this.settingsEvent = event;
    // set day type
    if (appointment.isMorning()) {
      this.settingsForm.get('daytype').setValue(this.optionsDayType[1].value);
    } else if (appointment.isAfternoon()) {
      this.settingsForm.get('daytype').setValue(this.optionsDayType[2].value);
    } else {
      this.settingsForm.get('daytype').setValue(this.optionsDayType[0].value);
    }
    // set locations
    const locations: Array<number> = appointment.getLocations();
    if (Array.isArray(this.locations)) {
      this.locations.forEach((location: FormLocationModel) => {
        location.control.setValue(false);
        if (!locations || locations.find(actual => actual === location.id)) {
          location.control.setValue(true);
        }
      });
    }
    this.settingsVisible = true;
  }

  public cancelSettings(): void {
    this.settingsVisible = false;
    this.settingsAppointment = null;
    this.resetForm();
  }

  public saveSettings(): void {
    const locations: Array<number> = [];
    this.locations.forEach((location: FormLocationModel) => {
      if (location.control?.value === true) {
        locations.push(location.id);
      }
    });
    if (locations.length === this.locations.length) {
      this.settingsAppointment.setLocations(null);
    } else {
      this.settingsAppointment.setLocations(locations);
    }
    this.settingsAppointment.setDayType(this.settingsForm.get('daytype').value);
    this.emitDayEvent({
      day: this.settingsAppointment,
      event: this.settingsEvent
    });
    this.settingsVisible = false;
    this.settingsAppointment = null;
    this.resetForm();
  }

  // holiday settings

  public showHolidaySettings(appointment: AppointmentModel, event: AppointmentDayEvent): void {
    this.holidaySettingsAppointment = appointment;
    this.holidaySettingsEvent = event;
    // init states checkboxes
    this.optionsStates?.forEach((state: FormStateModel) => {
      state.control.setValue(true);
    });
    this.holidaySettingsVisible = true;
  }

  public cancelHolidaySettings(): void {
    this.holidaySettingsVisible = false;
    this.holidaySettingsAppointment = null;
    this.resetForm();
  }

  public saveHolidaySettings(): void {
    if (this.holidaySettingsForm.valid === true) {
      const states: Array<string> = [];
      this.optionsStates?.forEach((state: FormStateModel) => {
        if (state.control?.value === true) {
          states.push(state.name);
        }
      });
      const name = this.holidaySettingsForm.get('name').value;
      if (states.length === this.optionsStates.length) {
        this.holidaySettingsAppointment.setHolidayData(name, null);
      } else {
        this.holidaySettingsAppointment.setHolidayData(name, states);
      }
      this.emitDayEvent({
        day: this.holidaySettingsAppointment,
        event: this.holidaySettingsEvent
      });
      this.cancelHolidaySettings();
    } else {
      FormHelper.markFormAsDirty(this.holidaySettingsForm);
    }
  }

  private resetForm(): void {
    this.settingsForm.reset();
    this.holidaySettingsForm.reset();
  }

  private getNewAppointment(date: any): AppointmentModel {
    const jsDate = new Date(date.year, date.month, date.day);
    const payload: any = {
      type: this.dayMode?.key,
      day: moment(jsDate).format('Y-MM-DD'),
      deletable: true,
      status: 'available',
      ac: null
    };
    const appointment = new AppointmentModel().deserialize(payload);
    appointment.setTranslocoService(this.transloco);
    return appointment;
  }

  private isAppointmentSelectable(appointment: AppointmentModel | null): boolean {
    if (this.isAvailabilityMode() === true) {
      // availabilities
      if (appointment === null) {
        return true;
      } else if (appointment.type === AppointmentType.Hiringday) {
        return true;
      } else if (appointment.type === AppointmentType.Holiday) {
        if (appointment.isLocalHoliday()) {
          return true;
        } else {
          this.ui.toastInfo(this.transloco.translate('appointments.calendar.selection_of_holidays_not_possible'));
        }
      }
    } else if (this.isSpecialdaysMode() === true) {
      // specialdays
      if (appointment?.type === AppointmentType.Holiday) {
        return appointment === null;
      } else {
        return appointment === null || appointment.isLocalHoliday();
      }
    }
    return false;
  }

  private isAppointmentDeletable(appointment: AppointmentModel | null): boolean {
    if (appointment === null) {
      return false;
    }
    if (this.isAvailabilityMode() === true) {
      return !appointment.isHiringday() && !appointment.isHoliday() && appointment?.deletable === true;
    }
    return appointment?.deletable === true;
  }

  private getAppointment(date: any): AppointmentModel | null {
    return (
      this.dayMapping[date.year] !== undefined &&
      this.dayMapping[date.year][date.month + 1] !== undefined &&
      this.dayMapping[date.year][date.month + 1][date.day] !== undefined
    ) ? this.dayMapping[date.year][date.month + 1][date.day] : null;
  }

  private isAvailabilityMode(): boolean {
    return this.dayMode?.key === AppointmentCalendarDayMode.Availability;
  }

  private isSpecialdaysMode(): boolean {
    return this.dayMode?.key === AppointmentCalendarDayMode.Holiday || this.dayMode?.key === AppointmentCalendarDayMode.Hiringday;
  }

  private buildStates(): void {
    if (Array.isArray(this.states)) {
      this.optionsStates = this.states.map((state: any) => {
        const newState = new FormStateModel().deserialize({ name: state });
        newState.control = new UntypedFormControl(true);
        return newState;
      });
    }
  }

  private buildMonths(): void {
    this.calandarMinDate = (this.minDate) ? moment(this.minDate).toDate() : null;
    if (this.min && this.max) {
      const min: any = moment(this.min + '-01');
      const max: any = moment(this.max + '-01');
      const months: Array<Date> = [];
      while (min.isBefore(max)) {
        months.push(min.toDate());
        min.add(1, 'months');
      }
      this.months = months.slice(this.actualMonth, this.actualMonth + this.size);
    }
  }

  private buildDayMapping(): void {
    this.dayMapping = {};
    if (this.type === AppointmentCalendarType.Availabilities) {
      this.days = [...this.specialdays, ...this.days];
    }
    this.days.forEach((appointment: AppointmentModel) => {
      const date = moment(appointment.day);
      const year = date.format('Y');
      const month = date.format('M');
      const day = date.format('D');
      if (this.dayMapping[year] === undefined) {
        this.dayMapping[year] = {};
      }
      if (this.dayMapping[year][month] === undefined) {
        this.dayMapping[year][month] = {};
      }
      this.dayMapping[year][month][day] = appointment;
    });
  }

  private initForm(): void {
    this.locations.forEach((location: FormLocationModel) => {
      location.control = new UntypedFormControl('');
      this.settingsForm.addControl(location.name, location.control);
    });
  }

  private buildTimingInfos(): void {
    if (this.timing) {
      if (this.timing.day_start && this.timing.day_end) {
        // full
        const start = moment().startOf('day').add(this.timing.day_start, 'hours');
        const end = moment().startOf('day').add(this.timing.day_end, 'hours');
        this.timingInfo.full = start.format('HH:mm') + ' - ' + end.format('HH:mm');
      }
      if (this.timing.day_start && this.timing.day_center) {
        // morning
        const start = moment().startOf('day').add(this.timing.day_start, 'hours');
        const end = moment().startOf('day').add(this.timing.day_center, 'hours');
        if (this.timing.day_center_minutes > 0) {
          end.add(this.timing.day_center_minutes, 'minutes');
        }
        this.timingInfo.morning = start.format('HH:mm') + ' - ' + end.format('HH:mm');
      }
      if (this.timing.day_end && this.timing.day_center) {
        // afternoon
        const start = moment().startOf('day').add(this.timing.day_center, 'hours');
        if (this.timing.day_center_minutes > 0) {
          start.add(this.timing.day_center_minutes, 'minutes');
        }
        const end = moment().startOf('day').add(this.timing.day_end, 'hours');
        this.timingInfo.afternoon = start.format('HH:mm') + ' - ' + end.format('HH:mm');
      }
    }
  }

}
